import type { TableMetadataType } from "@/types/tableMetadata";
import type { TableFilters } from "@/types/tableState.types";

type DashboardPageNameOptions = {
  datasetName: string;
  subjectId?: string;
  activeDataPage?: string;
  isTabbed: boolean;
};

export function getDashboardPageName({
  datasetName,
  subjectId,
  activeDataPage,
  isTabbed,
}: DashboardPageNameOptions): string {
  const basePageName = subjectId ? `${datasetName}-${subjectId}` : datasetName;

  if (!isTabbed) return basePageName;

  return `${basePageName}-${activeDataPage || "all"}`;
}

export function getDashboardMetadataForPage(
  metadata: TableMetadataType,
  activeDataPage?: string,
): TableMetadataType {
  const pageMetadata = activeDataPage && metadata.pages?.[activeDataPage];

  if (!pageMetadata) return metadata;

  return {
    ...metadata,
    columns: pageMetadata.columns,
    filters: pageMetadata.filters,
  };
}

export function omitDashboardFilterKeys<T>(
  filters: Record<string, T>,
  keysToOmit: readonly string[],
): Record<string, T> {
  if (keysToOmit.length === 0) return filters;

  const omittedKeys = new Set(keysToOmit);
  return Object.fromEntries(
    Object.entries(filters).filter(([key]) => !omittedKeys.has(key)),
  );
}

/**
 * Fixed `site` filter for a site-scoped screen (a site-profile subview).
 *
 * Kept as one helper so the table, download and plot requests all name the site
 * boundary the same way, and so callers spread it last — a site scope is a hard
 * boundary that a preset, pinned or user-chosen filter must not widen.
 */
export function buildSiteScopeFilter(siteId?: string): TableFilters {
  return siteId ? { site: [siteId] } : {};
}

export function buildDashboardPlotFilters(
  filters: TableFilters,
  subjectId?: string,
  pinnedFilters?: TableFilters,
): TableFilters {
  const plotFilters = subjectId
    ? omitDashboardFilterKeys(filters, ["subject"])
    : filters;

  return { ...plotFilters, ...(pinnedFilters ?? {}) };
}

export function getDashboardPlotMetadata(
  activeMetadata: TableMetadataType,
  globalMetadata: TableMetadataType,
  dataPageNameField?: string,
): TableMetadataType {
  const pageFilterMetadata = dataPageNameField
    ? globalMetadata.filters[dataPageNameField]
    : undefined;

  if (!pageFilterMetadata) return activeMetadata;

  return {
    ...activeMetadata,
    filters: {
      ...activeMetadata.filters,
      [dataPageNameField]: pageFilterMetadata,
    },
  };
}
